import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Check, Copy, Megaphone, Plus, Search, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import { PageHeader } from "~/components/shell/app-shell";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { ConfirmDialog } from "~/components/ui/confirm-dialog";
import { EmptyState, Skeleton } from "~/components/ui/feedback";
import { Input } from "~/components/ui/input";
import { Tooltip } from "~/components/ui/menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { toast } from "~/components/ui/toaster";
import {
  campaignActions,
  campaignKeys,
  campaignListQuery,
  type Campaign,
} from "~/features/campaigns/api";
import { campaignLabel } from "~/features/campaigns/format";
import { savedCampaignUrl } from "~/features/campaigns/link";
import { leadKeys } from "~/features/leads/api";
import { errorMessage } from "~/lib/auth";
import { formatDate } from "~/lib/format";
import { useCan } from "~/lib/use-can";

export const Route = createFileRoute("/_app/utm-campaigns")({
  validateSearch: z.object({ q: z.string().optional().catch(undefined) }),
  component: CampaignsPage,
});

const leadCount = (count: number) => `${count} lead${count === 1 ? "" : "s"}`;

function CampaignsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [query, setQuery] = useState(search.q ?? "");
  const [deleting, setDeleting] = useState<Campaign | null>(null);
  const queryClient = useQueryClient();
  const canDelete = useCan()("campaign", "delete");
  const campaigns = useQuery(campaignListQuery(search.q ?? ""));
  const rows = campaigns.data?.campaigns ?? [];

  useEffect(() => {
    const handle = setTimeout(() => {
      if ((search.q ?? "") !== query.trim()) {
        void navigate({ search: { q: query.trim() || undefined }, replace: true });
      }
    }, 300);
    return () => clearTimeout(handle);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- only react to typing
  }, [query]);

  const remove = useMutation({
    mutationFn: (campaign: Campaign) => campaignActions.remove(campaign.id),
    onSuccess: async (_data, campaign) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: campaignKeys.all }),
        queryClient.invalidateQueries({ queryKey: leadKeys.all }),
      ]);
      toast.success(`${campaignLabel(campaign)} was deleted.`);
      setDeleting(null);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <>
      <PageHeader
        title="Campaigns"
        description="Every campaign saved from the link builder, and how many leads each brought in."
        actions={
          <Button size="sm" asChild>
            <Link to="/settings/lead-form">
              <Plus /> New campaign link
            </Link>
          </Button>
        }
      />

      <Card padding="sm" className="mb-4">
        <div className="relative">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-muted"
            aria-hidden
          />
          <Input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by source, medium, campaign or keyword"
            aria-label="Search campaigns"
            className="h-9 pl-9"
          />
        </div>
      </Card>

      <Card padding="none" className="overflow-hidden">
        {campaigns.isPending ? (
          <div className="flex flex-col gap-3 p-5">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : campaigns.isError ? (
          <EmptyState
            icon={<Megaphone />}
            title="Couldn't load campaigns"
            description={errorMessage(campaigns.error)}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Megaphone />}
            title={search.q ? "No campaigns match" : "No campaigns yet"}
            description={
              search.q
                ? "Try a different search."
                : "Create a tagged link in Settings → Lead form. Each saved link appears here."
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-14">S.N.</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="hidden sm:table-cell">Medium</TableHead>
                <TableHead className="hidden md:table-cell">Keywords</TableHead>
                <TableHead className="text-right">Leads</TableHead>
                <TableHead className="hidden lg:table-cell">Created</TableHead>
                <TableHead className={canDelete ? "w-24" : "w-12"}>
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((campaign, index) => (
                <TableRow key={campaign.id}>
                  <TableCell data-tabular className="text-fg-muted">
                    {index + 1}
                  </TableCell>
                  <TableCell className="font-medium text-fg">
                    {campaign.name ?? <span className="text-fg-muted">No name</span>}
                  </TableCell>
                  <TableCell className="text-fg-secondary">{campaign.source ?? "—"}</TableCell>
                  <TableCell className="hidden text-fg-secondary sm:table-cell">
                    {campaign.medium ?? "—"}
                  </TableCell>
                  <TableCell className="hidden max-w-72 md:table-cell">
                    {campaign.keywords.length ? (
                      <span className="flex flex-wrap gap-1">
                        {campaign.keywords.map((keyword) => (
                          <Badge key={keyword}>{keyword}</Badge>
                        ))}
                      </span>
                    ) : (
                      <span className="text-fg-muted">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right" data-tabular>
                    {campaign.leadCount > 0 ? (
                      <Link
                        to="/leads"
                        search={{ campaignId: campaign.id }}
                        className="font-medium text-primary hover:underline"
                        aria-label={`${leadCount(campaign.leadCount)} from ${campaignLabel(campaign)}`}
                      >
                        {campaign.leadCount}
                      </Link>
                    ) : (
                      <span className="text-fg-muted">0</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden text-fg-secondary lg:table-cell">
                    {formatDate(campaign.createdAt)}
                    {campaign.createdBy && (
                      <span className="block text-caption text-fg-muted">
                        by {campaign.createdBy}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <CopyLinkButton campaign={campaign} />
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Delete ${campaignLabel(campaign)}`}
                          onClick={() => setDeleting(campaign)}
                        >
                          <Trash2 />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {deleting && (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setDeleting(null)}
          title={`Delete ${campaignLabel(deleting)}?`}
          description={
            deleting.leadCount
              ? `${leadCount(deleting.leadCount)} came from this campaign. They'll keep their tags but won't be linked to it any more. Links already shared keep working, but new leads from them won't be linked either.`
              : "No leads came from this campaign. Links already shared keep working, but new leads from them won't be linked to a campaign."
          }
          confirmLabel="Delete campaign"
          destructive
          loading={remove.isPending}
          onConfirm={() => remove.mutate(deleting)}
        />
      )}
    </>
  );
}

/** Copies the campaign's tagged form link (as the link builder made it). */
function CopyLinkButton({ campaign }: { campaign: Campaign }) {
  const [copied, setCopied] = useState(false);
  const name = campaignLabel(campaign);
  return (
    <Tooltip content={copied ? "Copied" : "Copy campaign link"}>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={copied ? `Link for ${name} copied` : `Copy link for ${name}`}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(savedCampaignUrl(campaign));
            setCopied(true);
            toast.success(`Link for ${name} copied.`);
            setTimeout(() => setCopied(false), 2000);
          } catch {
            toast.error("Couldn't copy. Open Settings → Lead form to build the link instead.");
          }
        }}
      >
        {copied ? <Check className="text-success" /> : <Copy />}
      </Button>
    </Tooltip>
  );
}
